import {
  PaginationState,
  PaginationDefaultOptions,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DEFAULT_OPTIONS = [10, 20, 50];
export const DEFAULT_PAGINATION_CONFIG = {
  page: 0,
  size: DEFAULT_OPTIONS[0],
  pageCount: undefined,
};

type Params = Partial<{
  shouldPersist: boolean;
  defaultRowsPerPageOptions: number[];
  totalRows: number;
}>;

export const usePagination = (params?: Params) => {
  const {
    shouldPersist = false,
    totalRows,
    defaultRowsPerPageOptions = DEFAULT_OPTIONS,
  } = params || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPagination: PaginationState = useMemo(() => {
    if (!shouldPersist) {
      return {
        pageIndex: DEFAULT_PAGINATION_CONFIG.page,
        pageSize: defaultRowsPerPageOptions[0],
        pageCount: DEFAULT_PAGINATION_CONFIG.pageCount,
      };
    }

    let pageToSet = Number(searchParams.get('page'));
    const rowsPerPageParam = Number(searchParams.get('rowsPerPage'));

    const pageSize = defaultRowsPerPageOptions.includes(rowsPerPageParam)
      ? rowsPerPageParam
      : defaultRowsPerPageOptions[0];
    const pageCount = totalRows ? Math.ceil(totalRows / pageSize) : undefined;

    if (!pageToSet || pageToSet < 1 || (pageCount && pageToSet > pageCount)) {
      pageToSet = 1;
    }

    return {
      pageIndex: pageToSet - 1,
      pageSize,
      pageCount,
    };
  }, [defaultRowsPerPageOptions, searchParams, shouldPersist, totalRows]);

  const [pagination, setPagination] =
    useState<PaginationState>(initialPagination);

  const handlePaginationChange: PaginationDefaultOptions['onPaginationChange'] =
    useCallback(
      (updaterOrValue) => {
        if (shouldPersist) {
          const newPagination =
            typeof updaterOrValue === 'function'
              ? updaterOrValue(pagination)
              : updaterOrValue;

          if (isEqual(newPagination, pagination)) {
            return;
          }

          setSearchParams(
            (params) => {
              params.set('page', String(newPagination.pageIndex + 1));
              params.set('rowsPerPage', String(newPagination.pageSize));
              return params;
            },
            { replace: true }
          );
        }
        return setPagination(updaterOrValue);
      },
      [pagination, setSearchParams, shouldPersist]
    );

  return {
    pagination,
    setPagination: handlePaginationChange,
  };
};
